'use client';
import React, { useEffect, useRef, useState } from 'react';

const stats = [
  { id: 'stat-events', value: 2400000, label: 'Events Monitored Daily', suffix: '+', format: 'abbr' },
  { id: 'stat-countries', value: 192, label: 'Countries Covered', suffix: '', format: 'num' },
  { id: 'stat-latency', value: 47, label: 'Avg. Detection Latency (sec)', suffix: 's', format: 'num' },
  { id: 'stat-clients', value: 340, label: 'Enterprise Clients', suffix: '+', format: 'num' },
];

function formatValue(value: number, format: string): string {
  if (format === 'abbr') {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  }
  return value.toString();
}

function AnimatedCounter({ target, format, suffix }: { target: number; format: string; suffix: string }) {
  const [current, setCurrent] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const duration = 1500;
          const steps = 60;
          const increment = target / steps;
          let step = 0;
          const timer = setInterval(() => {
            step++;
            setCurrent(Math.min(Math.round(increment * step), target));
            if (step >= steps) clearInterval(timer);
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return (
    <span ref={ref} className="font-tabular">
      {formatValue(current, format)}{suffix}
    </span>
  );
}

export default function StatsSection() {
  return (
    <section className="py-16 border-y border-border bg-muted/30">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.id} className="text-center">
              <div className="text-3xl xl:text-4xl font-bold gradient-text-primary mb-2">
                <AnimatedCounter target={stat.value} format={stat.format} suffix={stat.suffix} />
              </div>
              <p className="text-sm text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}